'use client';
import React, { useEffect, useState } from 'react';
import { Users, Building2, CalendarCheck, AlertTriangle, Clock, CreditCard } from 'lucide-react';
import { dashboardService, DashboardStats } from '@/lib/services/dashboardService';
import Icon from '@/components/ui/AppIcon';


function formatKsh(amount: number): string {
  if (amount >= 1000) {
    return `Ksh ${amount.toLocaleString('en-KE', { maximumFractionDigits: 0 })}`;
  }
  return `Ksh ${amount}`;
}

export default function DashboardMetrics() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dashboardService.getStats().then((s) => {
      setStats(s);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const attendanceRate = stats && stats.todayExpected > 0
    ? ((stats.todayPresent + stats.todayLate) / stats.todayExpected * 100).toFixed(1)
    : '0.0';

  const metrics = [
    {
      id: 'metric-active-workers',
      label: 'Active Workers',
      value: loading ? '—' : String(stats?.activeWorkers ?? 0),
      sub: `across ${stats?.activeSites ?? 0} active site${(stats?.activeSites ?? 0) !== 1 ? 's' : ''}`,
      icon: Users,
      trend: loading ? 'Loading…' : `${stats?.activeWorkers ?? 0} registered`,
      trendUp: true,
      color: 'text-primary',
      bg: 'bg-primary/10',
    },
    {
      id: 'metric-today-attendance',
      label: "Today\'s Attendance Rate",
      value: loading ? '—' : `${attendanceRate}%`,
      sub: loading ? '…' : `${(stats?.todayPresent ?? 0) + (stats?.todayLate ?? 0)} of ${stats?.todayExpected ?? 0} checked in`,
      icon: CalendarCheck,
      trend: loading ? 'Loading…' : `${stats?.todayPresent ?? 0} present · ${stats?.todayLate ?? 0} late`,
      trendUp: true,
      color: 'text-green-700',
      bg: 'bg-green-50',
    },
    {
      id: 'metric-absent-today',
      label: 'Absent Today',
      value: loading ? '—' : String(stats?.todayAbsent ?? 0),
      sub: loading ? '…' : `${stats?.todayAbsent ?? 0} absent from today's records`,
      icon: AlertTriangle,
      trend: (stats?.todayAbsent ?? 0) > 0 ? 'Requires attention' : 'All accounted for',
      trendUp: (stats?.todayAbsent ?? 0) === 0,
      color: 'text-red-600',bg: 'bg-red-50',
    },
    {
      id: 'metric-active-sites',label: 'Active Sites',value: loading ? '—' : String(stats?.activeSites ?? 0),sub: 'Currently active construction sites',icon: Building2,trend: loading ? 'Loading…' : `${stats?.activeSites ?? 0} site${(stats?.activeSites ?? 0) !== 1 ? 's' : ''} running`,
      trendUp: true,
      color: 'text-blue-700',bg: 'bg-blue-50',
    },
    {
      id: 'metric-pending-pay',label: 'Pending Payments',value: loading ? '—' : formatKsh(stats?.pendingPaymentsTotal ?? 0),
      sub: loading ? '…' : `${stats?.pendingPaymentsCount ?? 0} worker${(stats?.pendingPaymentsCount ?? 0) !== 1 ? 's' : ''} awaiting payment`,
      icon: CreditCard,
      trend: (stats?.pendingPaymentsCount ?? 0) > 0 ? 'Action required' : 'All payments settled',
      trendUp: (stats?.pendingPaymentsCount ?? 0) === 0,
      color: 'text-amber-700',bg: 'bg-amber-50',
    },
    {
      id: 'metric-late-today',label: 'Late Arrivals Today',value: loading ? '—' : String(stats?.todayLate ?? 0),sub: 'Checked in after scheduled time',icon: Clock,trend: loading ? 'Loading…' : `${stats?.todayLate ?? 0} late check-in${(stats?.todayLate ?? 0) !== 1 ? 's' : ''} today`,
      trendUp: (stats?.todayLate ?? 0) === 0,
      color: 'text-purple-700',bg: 'bg-purple-50',
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 2xl:grid-cols-6 gap-4">
      {metrics?.map((m) => {
        const Icon = m?.icon;
        return (
          <div
            key={m?.id}
            className="bg-card border border-border rounded-xl p-4 flex flex-col gap-3 col-span-1"
          >
            <div className="flex items-center justify-between">
              <p className="text-[12px] font-600 uppercase tracking-wide text-muted-foreground">{m?.label}</p>
              <span className={`p-1.5 rounded-lg ${m?.bg}`}>
                <Icon size={15} className={m?.color} />
              </span>
            </div>
            <div>
              <p className={`text-2xl font-700 tabular-nums ${m?.color}`}>{m?.value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{m?.sub}</p>
            </div>
            <p className={`text-[11px] font-500 ${m?.trendUp ? 'text-green-600' : 'text-red-500'}`}>
              {m?.trend}
            </p>
          </div>
        );
      })}
    </div>
  );
}